import { Tab } from "@headlessui/react"
import clsx from "clsx"
import { AnimatePresence, motion } from "framer-motion"
import { GatsbyImage } from "gatsby-plugin-image"
import HTMLReactParser from "html-react-parser"
import React, { Fragment, useEffect, useRef, useState } from "react"
import { useDebouncedCallback } from "use-debounce"

import RichText from "../richText/Parser"
import { AppScreen } from "./AppScreen"
import { CircleBackground } from "./CircleBackground"
import { Container } from "./Container"
import { PhoneFrame } from "./PhoneFrame"

const MotionAppScreenBody = motion(AppScreen.Body)

const maxZIndex = 2147483647

const bodyVariantBackwards = {
  opacity: 0.4,
  scale: 0.8,
  zIndex: 0,
  filter: "blur(4px)",
  transition: { duration: 0.4 },
}

const bodyVariantForwards = custom => ({
  y: "100%",
  zIndex: maxZIndex - custom.changeCount,
  transition: { duration: 0.4 },
})

const bodyAnimation = {
  initial: "initial",
  animate: "animate",
  exit: "exit",
  variants: {
    initial: custom =>
      custom.isForwards ? bodyVariantForwards(custom) : bodyVariantBackwards,
    animate: custom => ({
      y: "0%",
      opacity: 1,
      scale: 1,
      zIndex: maxZIndex / 2 - custom.changeCount,
      filter: "blur(0px)",
      transition: { duration: 0.4 },
    }),
    exit: custom =>
      custom.isForwards ? bodyVariantBackwards : bodyVariantForwards(custom),
  },
}

function usePrevious(value) {
  const ref = useRef()

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref.current
}

function FeaturesDesktop({ items }) {
  const [changeCount, setChangeCount] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const prevIndex = usePrevious(selectedIndex)
  const isForwards = prevIndex === undefined ? true : selectedIndex > prevIndex

  const onChange = useDebouncedCallback(
    selectedIndex => {
      setSelectedIndex(selectedIndex)
      setChangeCount(changeCount => changeCount + 1)
    },
    100,
    { leading: true }
  )

  return (
    <Tab.Group
      as="div"
      className="grid grid-cols-12 items-center gap-8 lg:gap-16 xl:gap-24"
      selectedIndex={selectedIndex}
      onChange={onChange}
      vertical
    >
      <Tab.List className="relative z-10 order-last col-span-6 space-y-6">
        {items.map((item, itemIndex) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={itemIndex}
            className="relative rounded-2xl transition-colors hover:bg-charlestonGreen-100/50"
          >
            {itemIndex === selectedIndex && (
              <motion.div
                layoutId="activeBackground"
                className="absolute inset-0 bg-charlestonGreen-100"
                initial={{ borderRadius: 16 }}
              />
            )}
            <div className="relative z-10 p-8 mb-6 ">
              {item.contentIcon?.localFile?.childImageSharp
                ?.gatsbyImageData && (
                <div className="h-8 w-8">
                  <span
                    className={`w-8 h-8 text-3xl leading-8 text-charlestonGreen-500 bx ${
                      item.textIcon
                    } ${itemIndex === selectedIndex && "text-gray-400"}`}
                  >
                    <GatsbyImage
                      objectFit="contain"
                      className="max-w-full"
                      image={
                        item.contentIcon?.localFile?.childImageSharp
                          ?.gatsbyImageData
                      }
                      alt={item?.contentIcon?.altText}
                    />
                  </span>
                </div>
              )}
              <h3 className="text-xl font-semibold">
                <Tab className="text-left [&:not(:focus-visible)]:focus:outline-none">
                  <span className="absolute inset-0 rounded-2xl" />
                  {item.contentTitle}
                </Tab>
              </h3>

              {item.contentBody && (
                <RichText
                  stringHtml={item.contentBody}
                  option="standardOptions"
                />
              )}
            </div>
          </div>
        ))}
      </Tab.List>
      <div className="relative col-span-6">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <CircleBackground color="#13B5C8" className="animate-spin-slower" />
        </div>
        <PhoneFrame className="z-10 mx-auto w-full max-w-[366px]">
          <Tab.Panels as={Fragment}>
            <AnimatePresence
              initial={false}
              custom={{ isForwards, changeCount }}
            >
              {items.map((item, itemIndex) =>
                selectedIndex === itemIndex ? (
                  <Tab.Panel
                    static
                    key={item.contentTitle + changeCount}
                    className="col-start-1 row-start-1 flex focus:outline-offset-[32px] [&:not(:focus-visible)]:focus:outline-none"
                  >
                    <AppScreen item={item} className="w-full">
                      {/* custom = { isForwards, changeCount } */}

                      <MotionAppScreenBody
                        {...{
                          ...bodyAnimation,
                          custom: { isForwards, changeCount },
                        }}
                      >
                        <GatsbyImage
                          className="w-full"
                          image={
                            item.screenContent?.localFile?.childImageSharp
                              ?.gatsbyImageData
                          }
                          alt={item?.screenContent?.altText}
                        />
                      </MotionAppScreenBody>
                    </AppScreen>
                  </Tab.Panel>
                ) : null
              )}
            </AnimatePresence>
          </Tab.Panels>
        </PhoneFrame>
      </div>
    </Tab.Group>
  )
}

function FeaturesMobile({ items }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const slideContainerRef = useRef()
  const slideRefs = useRef([])

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      entries => {
        // eslint-disable-next-line no-restricted-syntax
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveIndex(slideRefs.current.indexOf(entry.target))
            break
          }
        }
      },
      {
        root: slideContainerRef.current,
        threshold: 0.6,
      }
    )

    // eslint-disable-next-line no-restricted-syntax
    for (const slide of slideRefs.current) {
      if (slide) {
        observer.observe(slide)
      }
    }

    return () => {
      observer.disconnect()
    }
  }, [slideContainerRef, slideRefs])

  return (
    <>
      <div
        ref={slideContainerRef}
        className="-mb-4 flex snap-x snap-mandatory -space-x-4 overflow-x-auto overscroll-x-contain scroll-smooth pb-4 [scrollbar-width:none] sm:-space-x-6 [&::-webkit-scrollbar]:hidden"
      >
        {items.map((item, itemIndex) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={itemIndex}
            ref={ref => {
              slideRefs.current[itemIndex] = ref
            }}
            className="w-full flex-none snap-center px-4 sm:px-6"
          >
            <div className="relative transform overflow-hidden rounded-2xl bg-charlestonGreen-100/95 px-5 py-6">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <CircleBackground
                  color="#13B5C8"
                  className={itemIndex % 2 === 1 ? "rotate-180" : undefined}
                />
              </div>
              <PhoneFrame className="relative mx-auto w-full max-w-[366px]">
                <AppScreen item={item} className="w-full">
                  <GatsbyImage
                    objectFit="contain"
                    className="max-w-full"
                    image={
                      item.screenContent?.localFile?.childImageSharp
                        ?.gatsbyImageData
                    }
                    alt={item?.screenContent?.altText}
                  />
                </AppScreen>
              </PhoneFrame>

              <div className="absolute inset-x-0 bottom-0 bg-charlestonGreen-100/95 p-6 backdrop-blur sm:p-10">
                {item.contentIcon?.localFile?.childImageSharp
                  ?.gatsbyImageData && (
                  <div className="h-8 w-8">
                    <GatsbyImage
                      objectFit="contain"
                      className="max-w-full"
                      image={
                        item.contentIcon?.localFile?.childImageSharp
                          ?.gatsbyImageData
                      }
                      alt={item?.contentIcon?.altText}
                    />
                  </div>
                )}
                {item.contentTitle && (
                  <h3 className="sm:mt-6 text-sm font-semibold sm:text-lg">
                    {item.contentTitle && HTMLReactParser(item.contentTitle)}
                  </h3>
                )}
                {item.contentBody && (
                  <RichText
                    stringHtml={item.contentBody}
                    option="standardOptions"
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 flex justify-center gap-3">
        {items.map((_, itemIndex) => (
          <button
            type="button"
            // eslint-disable-next-line react/no-array-index-key
            key={itemIndex}
            className={clsx(
              "relative h-1 w-4 rounded-full",
              itemIndex === activeIndex ? "bg-orangeSafe" : "bg-blue-400"
            )}
            aria-label={`Go to slide ${itemIndex + 1}`}
            onClick={() => {
              slideRefs.current[itemIndex].scrollIntoView({
                block: "nearest",
                inline: "nearest",
              })
            }}
          >
            <span className="absolute -inset-x-1.5 -inset-y-3" />
          </button>
        ))}
      </div>
    </>
  )
}

export default function IphoneModule({ component }) {
  const { richtitle, richSubtitle, screenItems } = component

  return (
    <section
      id="features"
      aria-label="Features for investing all your money"
      className=" py-12 sm:pt-12 sm:pb-24"
    >
      <Container>
        <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-3xl">
          {richtitle && (
            <RichText stringHtml={richtitle} option="sectionTitle" />
          )}
          {richSubtitle && (
            <RichText
              stringHtml={richSubtitle}
              option="standardOptions"
              dark={false}
              bigger={false}
            />
          )}
        </div>
      </Container>
      <div className="mt-16 md:hidden">
        <FeaturesMobile items={screenItems} />
      </div>
      <Container className="hidden md:mt-20 md:block">
        <FeaturesDesktop items={screenItems} />
      </Container>
    </section>
  )
}
